// Material-Bibliothek für den Wärmeausdehnungs-Modul (Task #27, Phase A).
//
// Quellen siehe Konzept doc/material-waerme-konzept.md § 3.3. Alpha-Werte
// sind lineare Ausdehnungskoeffizienten in 1/K (multipliziert mit 1e-6
// gegenüber der Tabelle dort — also als Zahl). Bei GF-verstärkten
// Thermoplasten ist Alpha der Längswert in Faserrichtung; quer kann
// 2–3× höher sein (siehe Tooltip in der UI).
//
// `hygroscopic`-Flag triggert in der UI einen Warnhinweis: bei PA-Typen
// kann die Feuchteaufnahme Längenänderungen in derselben Größenordnung
// wie der thermische Effekt verursachen. Die Modellierung selbst ist
// V2-Kandidat — V1 informiert den User nur.
//
// Werte sind absichtlich konservativ-runde Richtwerte aus DIN-Normen
// bzw. Herstellerdatenblättern, keine projektspezifischen Messdaten.

export const MATERIALS = {
  // ─── Stähle ───────────────────────────────────────────────────────
  steel_s235jr: {
    id: 'steel_s235jr', group: 'Stahl', name: 'Baustahl S235JR (1.0038)',
    alpha: 12e-6, source: 'DIN EN 10025-2'
  },
  steel_c45: {
    id: 'steel_c45', group: 'Stahl', name: 'Vergütungsstahl C45 (1.0503)',
    alpha: 12e-6, source: 'DIN EN 10083-2'
  },
  steel_42crmo4: {
    id: 'steel_42crmo4', group: 'Stahl', name: 'Vergütungsstahl 42CrMo4 (1.7225)',
    alpha: 12.5e-6, source: 'DIN EN 10083-3'
  },
  steel_100cr6: {
    id: 'steel_100cr6', group: 'Wälzlagerstahl', name: 'Wälzlagerstahl 100Cr6 (1.3505)',
    alpha: 11.5e-6, source: 'DIN EN ISO 683-17'
  },

  // ─── Edel- & Werkzeugstähle ──────────────────────────────────────
  stainless_1_4301: {
    id: 'stainless_1_4301', group: 'Edelstahl', name: 'X5CrNi18-10 (1.4301)',
    alpha: 16e-6, source: 'DIN EN 10088-3'
  },
  toolsteel_1_2379: {
    id: 'toolsteel_1_2379', group: 'Werkzeugstahl', name: 'X155CrVMo12-1 (1.2379)',
    alpha: 11e-6, source: 'DIN EN ISO 4957'
  },

  // ─── Gusswerkstoffe ───────────────────────────────────────────────
  cast_gjl200: {
    id: 'cast_gjl200', group: 'Grauguss', name: 'EN-GJL-200',
    alpha: 11e-6, source: 'DIN EN 1561'
  },

  // ─── NE-Metalle ───────────────────────────────────────────────────
  alu_6060: {
    id: 'alu_6060', group: 'Aluminium', name: 'EN AW-6060 (AlMgSi)',
    alpha: 23e-6, source: 'DIN EN 573'
  },
  alu_7075: {
    id: 'alu_7075', group: 'Aluminium', name: 'EN AW-7075 (AlZnMgCu)',
    alpha: 23.4e-6, source: 'DIN EN 573'
  },
  mg_az91: {
    id: 'mg_az91', group: 'Magnesium', name: 'AZ91 (Druckguss)',
    alpha: 26e-6, source: 'DIN EN 1753'
  },
  brass_cw508l: {
    id: 'brass_cw508l', group: 'Messing', name: 'CW508L (CuZn37)',
    alpha: 19e-6, source: 'DIN EN 12164'
  },
  copper_cudhp: {
    id: 'copper_cudhp', group: 'Kupfer', name: 'Cu-DHP (CW024A)',
    alpha: 17e-6, source: 'DIN EN 12449'
  },
  bronze_cc480k: {
    id: 'bronze_cc480k', group: 'Bronze', name: 'CC480K (CuSn5Zn5Pb5-C)',
    alpha: 18e-6, source: 'DIN EN 1982'
  },
  titanium_ti6al4v: {
    id: 'titanium_ti6al4v', group: 'Titan', name: 'Ti-6Al-4V (3.7165)',
    alpha: 8.6e-6, source: 'DIN 17851'
  },

  // ─── Kunststoffe (unverstärkt) ───────────────────────────────────
  plastic_pom_c: {
    id: 'plastic_pom_c', group: 'Kunststoff', name: 'POM-C',
    alpha: 110e-6, source: 'Herstellerangabe'
  },
  plastic_pom_h: {
    id: 'plastic_pom_h', group: 'Kunststoff', name: 'POM-H (Homopolymer)',
    alpha: 115e-6, source: 'Herstellerangabe'
  },
  plastic_pa6: {
    id: 'plastic_pa6', group: 'Kunststoff', name: 'PA6',
    alpha: 80e-6, hygroscopic: true, source: 'Herstellerangabe'
  },
  plastic_pa66: {
    id: 'plastic_pa66', group: 'Kunststoff', name: 'PA66',
    alpha: 80e-6, hygroscopic: true, source: 'Herstellerangabe'
  },
  plastic_pa12: {
    id: 'plastic_pa12', group: 'Kunststoff', name: 'PA12',
    alpha: 100e-6, hygroscopic: true, source: 'Herstellerangabe'
  },
  plastic_pe_hd: {
    id: 'plastic_pe_hd', group: 'Kunststoff', name: 'PE-HD',
    alpha: 200e-6, source: 'Herstellerangabe'
  },
  plastic_pp: {
    id: 'plastic_pp', group: 'Kunststoff', name: 'PP',
    alpha: 150e-6, source: 'Herstellerangabe'
  },
  plastic_ptfe: {
    id: 'plastic_ptfe', group: 'Kunststoff', name: 'PTFE',
    alpha: 120e-6, source: 'Herstellerangabe'
  },
  plastic_peek: {
    id: 'plastic_peek', group: 'Kunststoff', name: 'PEEK',
    alpha: 47e-6, source: 'Herstellerangabe'
  },
  plastic_pmma: {
    id: 'plastic_pmma', group: 'Kunststoff', name: 'PMMA',
    alpha: 70e-6, source: 'Herstellerangabe'
  },
  plastic_pc: {
    id: 'plastic_pc', group: 'Kunststoff', name: 'PC',
    alpha: 70e-6, source: 'Herstellerangabe'
  },
  plastic_abs: {
    id: 'plastic_abs', group: 'Kunststoff', name: 'ABS',
    alpha: 90e-6, source: 'Herstellerangabe'
  },
  plastic_pvdf: {
    id: 'plastic_pvdf', group: 'Kunststoff', name: 'PVDF',
    alpha: 130e-6, source: 'Herstellerangabe'
  },

  // ─── Kunststoffe (GF-verstärkt) ──────────────────────────────────
  // Alpha = Längswert (Faserrichtung). Quer typischerweise 2–3× höher.
  plastic_pa6_gf30: {
    id: 'plastic_pa6_gf30', group: 'GF-verstärkt', name: 'PA6 GF30',
    alpha: 30e-6, hygroscopic: true, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_pa6_gf50: {
    id: 'plastic_pa6_gf50', group: 'GF-verstärkt', name: 'PA6 GF50',
    alpha: 25e-6, hygroscopic: true, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_pa66_gf30: {
    id: 'plastic_pa66_gf30', group: 'GF-verstärkt', name: 'PA66 GF30',
    alpha: 30e-6, hygroscopic: true, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_pa66_gf50: {
    id: 'plastic_pa66_gf50', group: 'GF-verstärkt', name: 'PA66 GF50',
    alpha: 22e-6, hygroscopic: true, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_pbt_gf30: {
    id: 'plastic_pbt_gf30', group: 'GF-verstärkt', name: 'PBT GF30',
    alpha: 25e-6, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_pom_gf25: {
    id: 'plastic_pom_gf25', group: 'GF-verstärkt', name: 'POM GF25',
    alpha: 55e-6, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_peek_gf30: {
    id: 'plastic_peek_gf30', group: 'GF-verstärkt', name: 'PEEK GF30',
    alpha: 22e-6, anisotropic: true, source: 'Herstellerangabe'
  },
  plastic_pps_gf40: {
    id: 'plastic_pps_gf40', group: 'GF-verstärkt', name: 'PPS GF40',
    alpha: 19e-6, anisotropic: true, source: 'Herstellerangabe'
  },
};

// Bezugstemperatur der Norm-Maßangaben (ISO 1). Wird hier als Konstante
// gepflegt, damit die Berechnungslogik nicht mit Magic Numbers arbeitet.
// In V2 evtl. konfigurierbar pro Projekt (z.B. Halbleiter-Labor 23 °C).
export const REFERENCE_TEMP_C = 20;

// Anzeige-Reihenfolge der Material-Gruppen im Dropdown. Stahl ganz oben,
// weil das mit Abstand der häufigste Werkstoff im Maschinenbau ist.
export const MATERIAL_GROUP_ORDER = [
  'Stahl',
  'Wälzlagerstahl',
  'Edelstahl',
  'Werkzeugstahl',
  'Grauguss',
  'Aluminium',
  'Magnesium',
  'Messing',
  'Kupfer',
  'Bronze',
  'Titan',
  'Kunststoff',
  'GF-verstärkt',
];

/**
 * Liefert das Material-Objekt zu einer ID oder null. Tolerant gegenüber
 * undefined/null/leerem String, sodass die Berechnung graceful auf "kein
 * Material" zurückfällt, wenn ein Element eine inzwischen entfernte ID
 * referenziert (z.B. aus älteren Projekt-Saves).
 */
export function findMaterial(id) {
  if (!id || typeof id !== 'string') return null;
  return MATERIALS[id] ?? null;
}
